import { API_URL } from './config';
import React, { useState, useRef, useEffect } from 'react';
import { getToken } from './auth';
import { deserializePhotoDraft, serializePhotoDraft, sanitizePhotoSelection } from './photoDraftUtils';

// Función optimizada para comprimir imágenes en dispositivos de gama baja.
// Para Android con hardware limitado, se reduce agresivamente la dimensión y calidad para
// evitar saturar la memoria del navegador antes de subir las fotos.
const getLowEndAndroidProfile = () => {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const lowCpu = typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;
  const lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 2;
  const smallScreen = typeof window !== 'undefined' && window.innerWidth <= 480;
  const isLowEnd = isAndroid && (lowCpu || lowMemory || smallScreen);

  return {
    isLowEnd,
    maxWidth: isLowEnd ? 640 : 1200,
    maxHeight: isLowEnd ? 640 : 1200,
    quality: isLowEnd ? 0.55 : 0.95
  };
};

const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.95) => {
  const profile = getLowEndAndroidProfile();
  const finalMaxWidth = profile.isLowEnd ? profile.maxWidth : maxWidth;
  const finalMaxHeight = profile.isLowEnd ? profile.maxHeight : maxHeight;
  const finalQuality = profile.isLowEnd ? profile.quality : quality;

  return new Promise((resolve, reject) => {
    const processBitmap = (source) => {
      try {
        let width = source.width;
        let height = source.height;

        if (width > height) {
          if (width > finalMaxWidth) {
            height = Math.round((height * finalMaxWidth) / width);
            width = finalMaxWidth;
          }
        } else if (height > finalMaxHeight) {
          width = Math.round((width * finalMaxHeight) / height);
          height = finalMaxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });

        if (!ctx) {
          reject(new Error('No se pudo crear el contexto del canvas.'));
          return;
        }

        ctx.drawImage(source, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            try {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              canvas.width = 0;
              canvas.height = 0;
            } catch (e) {}

            if (!blob) {
              reject(new Error('Error al comprimir imagen'));
              return;
            }

            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            const compressedFile = new File([blob], `${fileNameWithoutExt}.webp`, {
              type: 'image/webp',
              lastModified: Date.now()
            });

            if (typeof source.close === 'function') {
              source.close();
            }
            resolve(compressedFile);
          },
          'image/webp',
          finalQuality
        );
      } catch (error) {
        reject(new Error('Error al procesar la imagen'));
      }
    };

    if (typeof createImageBitmap === 'function') {
      createImageBitmap(file)
        .then(processBitmap)
        .catch(() => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => processBitmap(img);
            img.onerror = () => reject(new Error('Error loading image'));
          };
          reader.onerror = () => reject(new Error('Error reading file'));
        });
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => processBitmap(img);
      img.onerror = () => reject(new Error('Error loading image'));
    };
    reader.onerror = () => reject(new Error('Error reading file'));
  });
};

// Traducciones locales (puedes migrar a un sistema global si lo deseas)
const t = {
  en: {
    apartment: 'Apartment',
    perimeter: 'Perimeter',
    noApply: 'N/A',
    treatment: 'Treatment',
    pests: 'Pests',
    visitDetails: 'Visit Details',
    refused: 'Refused',
    notOpened: 'Not Opened',
    managerPresent: 'Manager Present',
    infestationLevel: 'Infestation Level',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    areasToTreat: 'Areas to Treat',
    kitchen: 'Kitchen',
    livingRoom: 'Living Room',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    garage: 'Garage',
    laundry: 'Laundry',
    exterior: 'Exterior',
    trashbins: 'Trash bins',
    preparation: 'Preparation',
    clean: 'Clean',
    dirty: 'Dirty',
    notPrepared: 'Not Prepared',
    specialTreatment: 'Notes',
    observations: 'Observations',
    submit: 'Register Visit',
    success: 'Visit registered successfully! Redirecting...',
    errorNoAreas: 'Select at least one area to treat.',
    errorNoPreparation: 'Select at least one preparation option.',
    errorMinPhotos: 'You must attach at least 0 photos.',
    errorMaxPhotos: 'You can only attach up to 5 photos.',
    photos: 'Photos',
    addPhoto: 'Add Photo',
    processing: 'Processing...',
    compressingImages: 'Compressing images for optimal performance...',
    orderNumber: 'Order'
  },
  es: {
    apartment: 'Apartamento',
    perimeter: 'Perímetro',
    noApply: 'N/A',
    treatment: 'Tratamiento',
    pests: 'Plagas',
    visitDetails: 'Detalles de la Visita',
    refused: 'Rechazado',
    notOpened: 'No Abierto',
    managerPresent: 'Manager Presente',
    infestationLevel: 'Nivel de Infestación',
    low: 'Bajo',
    medium: 'Medio',
    high: 'Alto',
    areasToTreat: 'Áreas a Tratar',
    kitchen: 'Cocina',
    livingRoom: 'Sala',
    bedrooms: 'Dormitorios',
    bathrooms: 'Baños',
    garage: 'Garaje',
    laundry: 'Lavandería',
    exterior: 'Exterior',
    trashbins: 'Basureros',
    preparation: 'Preparación',
    clean: 'Limpio',
    dirty: 'Sucio',
    notPrepared: 'No Preparado',
    specialTreatment: 'Notas',
    observations: 'Observaciones',
    submit: 'Registrar atención',
    success: '¡Atención registrada correctamente! Redirigiendo...',
    errorNoAreas: 'Selecciona al menos un área a tratar.',
    errorNoPreparation: 'Selecciona al menos una opción de preparación.',
    errorMinPhotos: 'Debes adjuntar al menos 0 fotos.',
    errorMaxPhotos: 'Solo puedes adjuntar hasta 5 fotos.',
    photos: 'Fotografías',
    addPhoto: 'Agregar Foto',
    processing: 'Procesando...',
    compressingImages: 'Comprimiendo imágenes para óptimo rendimiento...',
    orderNumber: 'Orden'
  }
};

export default function ApartmentVisitForm({ order, apartment, pests = [], onSuccess, onBack, lang = 'en' }) {
  // Estado para la hora registrada de inicio de la orden
  const [orderStartTime, setOrderStartTime] = useState(null);
  const [orderTimezone, setOrderTimezone] = useState(null);
  // Log defensivo para detectar props indefinidos
  useEffect(() => {
    if (!order || !order.id) {
      console.error('ERROR: order o order.id indefinido en ApartmentVisitForm', order);
    }
    if (!apartment || !apartment.id) {
      console.error('ERROR: apartment o apartment.id indefinido en ApartmentVisitForm', apartment);
    }
  }, [order, apartment]);
  
  // Generar clave única para este apartamento
  const formStorageKey = `pwa_visitForm_${order?.id}_${apartment?.id}`;
  const pendingVisitKey = `pwa_visitPending_${order?.id}_${apartment?.id}`;

  // Persistencia mínima del estado de envío para evitar perder el draft si la red tarda o falla
  const writePendingVisitState = (status = 'saving') => {
    if (!order?.id || !apartment?.id) return;
    const payload = {
      orderId: order.id,
      apartmentId: apartment.id,
      status,
      updatedAt: Date.now(),
      snapshot: { ...form }
    };
    try {
      localStorage.setItem(pendingVisitKey, JSON.stringify(payload));
    } catch (e) {
      console.warn('No se pudo persistir el estado pendiente de la visita:', e);
    }
  };

  const clearPendingVisitState = () => {
    try {
      localStorage.removeItem(pendingVisitKey);
    } catch (e) {
      console.warn('No se pudo limpiar el estado pendiente de la visita:', e);
    }
  };
  
  // Inicializar estado del formulario desde localStorage si existe
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(formStorageKey);
      if (saved) {
        const savedForm = JSON.parse(saved);
        delete savedForm.supplies;
        return savedForm;
      }
    } catch (e) {
      console.error('Error al recuperar formulario guardado:', e);
    }
    return {
      refused: false,
      exterior: false,
      notOpened: false,
      managerPresent: false,
      infestation: '',
      areas: [],
      preparation: [],
      observations: ''
    };
  });
  
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const photoDraftKey = `pwa_visitPhotos_${order?.id}_${apartment?.id}`;
  const [photos, setPhotos] = useState(() => {
    try {
      const rawDraft = localStorage.getItem(photoDraftKey);
      return deserializePhotoDraft(rawDraft, 5);
    } catch (e) {
      return [];
    }
  });
  const [compressingPhotos, setCompressingPhotos] = useState(false);
  const [saving, setSaving] = useState(false); // Spinner de guardado
  const [uploadProgress, setUploadProgress] = useState(0); // Progreso de fotos
  const fileInputRef = useRef();
  const apartmentPests = Array.isArray(pests) ? pests : [];
  const photoPreviewUrlsRef = useRef(new Map());

  const revokePhotoPreviews = () => {
    photoPreviewUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    photoPreviewUrlsRef.current.clear();
  };

  const persistPhotoDraft = async (nextPhotos = []) => {
    try {
      const draft = await serializePhotoDraft(nextPhotos, 5);
      localStorage.setItem(photoDraftKey, JSON.stringify(draft));
    } catch (e) {
      console.warn('No se pudo persistir el borrador de fotos:', e);
    }
  };

  // Limpiar memoria de fotos cuando cambia el apartamento o cuando se fuerza el borrado fresco
  const cleanPhotoMemory = () => {
    revokePhotoPreviews();
    setPhotos([]);
    try {
      localStorage.removeItem(photoDraftKey);
    } catch (e) {}
    setCompressingPhotos(false);
    if (window.gc) setTimeout(() => window.gc(), 100);
  };
  
  useEffect(() => {
    if (!order?.id || !apartment?.id) return;

    try {
      const rawDraft = localStorage.getItem(photoDraftKey);
      const hydrated = deserializePhotoDraft(rawDraft, 5);
      setPhotos(hydrated);
    } catch (e) {
      setPhotos([]);
    }
  }, [order?.id, apartment?.id]);

  useEffect(() => {
    return () => {
      revokePhotoPreviews();
    };
  }, []);

  // Persistir formulario en localStorage cuando cambie
  useEffect(() => {
    if (formStorageKey) {
      localStorage.setItem(formStorageKey, JSON.stringify(form));
    }
  }, [form, formStorageKey]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: checked }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleAreaChange = area => {
    setForm(f => ({ ...f, areas: f.areas.includes(area) ? f.areas.filter(a => a !== area) : [...f.areas, area] }));
  };
  const handlePreparationChange = prep => {
    setForm(f => ({ ...f, preparation: f.preparation.includes(prep) ? f.preparation.filter(p => p !== prep) : [...f.preparation, prep] }));
  };
  const L = t[lang] || t.es;

  const validateForm = () => {
    if (form.areas.length === 0) return L.errorNoAreas;
    if (form.preparation.length === 0) return L.errorNoPreparation;
    if (photos.length > 5) return L.errorMaxPhotos;
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (compressingPhotos || saving) {
      setError(lang === 'en' ? 'Please wait for the photos to finish processing.' : 'Por favor espera a que las fotos terminen de procesarse.');
      return;
    }
    if (!order || !order.id) {
      setError('Error interno: orderId indefinido. No se puede registrar la visita.');
      console.error('ERROR: order o order.id indefinido al enviar visita', order);
      return;
    }
    if (!apartment || !apartment.id) {
      setError('Error interno: apartmentId indefinido. No se puede registrar la visita.');
      console.error('ERROR: apartment o apartment.id indefinido al enviar visita', apartment);
      return;
    }
    const err = validateForm();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setSuccess(false);
    setSaving(true);
    writePendingVisitState('saving');
    setUploadProgress(0);
    const apartmentStartTime = new Date().toISOString();
    const payload = {
      notes: form.observations,
      done: true,
      refused: form.refused,
      // exterior: form.areas.includes('exterior'),
      notOpened: form.notOpened,
      managerPresent: form.managerPresent,
      infestation: form.infestation,
      areas: form.areas,
      preparation: form.preparation,
      observations: form.observations,
      startTime: apartmentStartTime,
      status: 'attended',
      endTime: new Date().toLocaleString('sv-SE', { timeZone: order.settings?.timezone || 'America/Mexico_City' })
    };
    try {
      // 1️⃣ Enviar registro al endpoint correcto
      const response = await fetch(
        `${API_URL}/orders/${order.id}/apartments/${apartment.id}/visit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(payload)
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Error al guardar la visita");
      }
      const data = await response.json();
      const respData = data.data || data;
      const visitId = respData?.visitId || respData?.visit_id || respData?.id;
      if (!visitId) {
        throw new Error('No se recibió el ID de la visita.');
      }
      // 2️⃣ Subir fotos por lotes de 2 en paralelo (sin tocar backend ni chunks)
      let photoFilenames = [];
      if (photos && photos.length > 0) {
        let completedUploads = 0;
        const batchSize = 2;

        for (let i = 0; i < photos.length; i += batchSize) {
          const batch = photos.slice(i, i + batchSize);

          const batchResults = await Promise.all(batch.map(async (photo) => {
            const formData = new FormData();
            formData.append('files', photo);

            const uploadRes = await fetch(`${API_URL}/apartments/${apartment.id}/photos`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${getToken()}`
              },
              body: formData
            });

            const uploadData = await uploadRes.json();
            if (!uploadRes.ok) {
              throw new Error(uploadData?.error || 'Error al subir una foto');
            }

            completedUploads += 1;
            setUploadProgress(completedUploads);

            try {
              const url = URL.createObjectURL(photo);
              URL.revokeObjectURL(url);
            } catch (e) {}

            if (window.gc) setTimeout(() => window.gc(), 50);

            if (uploadData.files && uploadData.files.length > 0) {
              return uploadData.files[0];
            }
            return null;
          }));

          photoFilenames.push(...batchResults.filter(Boolean));
        }

        // Asociar fotos a la visita
        if (photoFilenames.length > 0) {
          const linkRes = await fetch(`${API_URL}/visits/${visitId}/photos`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getToken()}`
            },
            body: JSON.stringify({ files: photoFilenames })
          });
          const linkData = await linkRes.json();
          if (!linkRes.ok) {
            setSaving(false);
            throw new Error(linkData?.error || 'Error al asociar fotos a la visita.');
          }
        }
      }
      setSuccess(lang === 'en' ? 'Visit saved successfully.' : 'Visita guardada correctamente.');
      localStorage.removeItem(formStorageKey);
      localStorage.removeItem(photoDraftKey);
      clearPendingVisitState();
      setPhotos([]);
      setSaving(false);
      setUploadProgress(0);
      if (window.gc) window.gc();
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (e) {
      writePendingVisitState('failed');
      setError(e.message || 'Error al guardar la visita.');
      setSuccess(false);
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const areaOptions = ['kitchen', 'livingRoom', 'bedrooms', 'bathrooms', 'garage', 'laundry', 'trashbins', 'perimeter', 'noApply'];
  const prepOptions = ['clean', 'dirty', 'notPrepared'];

  // Manejo de fotos optimizado para dispositivos de gama baja
  // Mejoras: evitar crear/revocar URLs innecesarias, solo crear previews cuando se renderizan
  const handlePhotoChange = async (e) => {
    const files = sanitizePhotoSelection(Array.from(e.target.files || []), 5);
    if (e.target) e.target.value = '';
    if (files.length === 0) return;
    if (window.gc && typeof window.gc === 'function') {
      try { window.gc(); } catch (e) {}
    }
    if (photos.length >= 5) {
      alert(lang === 'en' ? 'Maximum 5 photos allowed for optimal performance.' : 'Máximo 5 fotos permitidas para óptimo rendimiento.');
      return;
    }
    setCompressingPhotos(true);
    const compressedFiles = [];
    try {
      for (let i = 0; i < files.length && (photos.length + compressedFiles.length) < 5; i++) {
        try {
          const compressed = await compressImage(files[i]);
          compressedFiles.push(compressed);
          await new Promise(resolve => setTimeout(resolve, 20));
        } catch (err) {
          console.error(`Error al comprimir imagen ${i + 1}:`, err);
        }
      }
      if (compressedFiles.length > 0) {
        const nextPhotos = [...photos, ...compressedFiles].slice(0, 5);
        revokePhotoPreviews();
        setPhotos(nextPhotos);
        await persistPhotoDraft(nextPhotos);
      }
      if (window.gc) setTimeout(() => window.gc(), 50);
    } catch (err) {
      console.error('Error al comprimir imágenes:', err);
      alert(lang === 'en' ? 'Error processing images. Please try with smaller photos or one at a time.' : 'Error al procesar las imágenes. Intenta con fotos más pequeñas o una por vez.');
    } finally {
      setCompressingPhotos(false);
    }
  };
  
  const handleRemovePhoto = idx => {
    const nextPhotos = photos.filter((_, i) => i !== idx);
    revokePhotoPreviews();
    setPhotos(nextPhotos);
    persistPhotoDraft(nextPhotos);
    if (window.gc) setTimeout(() => window.gc(), 50);
  };

  // Botón manual para limpiar memoria si ocurre error de memoria insuficiente
  const [showCleanMemory, setShowCleanMemory] = useState(false);
  useEffect(() => {
    if (error && /memoria|memory/i.test(error)) {
      setShowCleanMemory(true);
    } else {
      setShowCleanMemory(false);
    }
  }, [error]);

  // La orden solo debe actualizar su startTime si el snapshot actual aún no lo trae.
  // El startTime del apartamento se registra en el payload final de DONE y no al abrir el formulario.
  useEffect(() => {
    if (!order || !order.id || !apartment || !apartment.id) return;

    const incomingOrderStartTime = order?.startTime || order?.startedAt || order?.start_time;
    if (incomingOrderStartTime) {
      setOrderStartTime(incomingOrderStartTime);
      setOrderTimezone(order?.timezone || null);
      return;
    }

    const cacheKey = `pwa_order_start_time_${order.id}`;

    try {
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && cached.startTime) {
          setOrderStartTime(cached.startTime);
          setOrderTimezone(cached.timezone || null);
          return;
        }
      }
    } catch (e) {
      // Ignorar caché corrupto y continuar con la petición real.
    }

    let isMounted = true;

    const fetchStartTime = async () => {
      try {
        const res = await fetch(`${API_URL}/orders/${order.id}/start-time`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({})
        });

        if (!res.ok) {
          throw new Error('No se pudo recuperar la hora de inicio');
        }

        const data = await res.json();
        if (!data || !data.startTime) return;

        const nextValue = {
          startTime: data.startTime,
          timezone: data.timezone || null
        };

        try {
          localStorage.setItem(cacheKey, JSON.stringify(nextValue));
        } catch (e) {
          // Ignorar si el almacenamiento falla.
        }

        if (isMounted) {
          setOrderStartTime(data.startTime);
          setOrderTimezone(data.timezone || null);
        }
      } catch (e) {
        // Si falla, no bloquear la UI ni forzar reintentos por cada render.
      }
    };

    fetchStartTime();

    return () => {
      isMounted = false;
    };
  }, [order?.id, order?.startTime, order?.startedAt, order?.start_time, apartment?.id]);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 500, margin: '0 auto', marginTop: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ textAlign: 'center', marginBottom: 0, paddingBottom: 0, color: '#797a7a' }}>
            {L.orderNumber} #
            {order?.orderNumber || order?.orderNumber || order?.id}
          </h2>
          <h2 style={{ textAlign: 'center', margin: '4px 0 8px' }}>{L.apartment} {apartment?.description || apartment?.name || apartment?.id}</h2>
          {orderStartTime && (
            <div style={{ textAlign: 'center', fontSize: 15, color: '#2563eb', marginTop: 2 }}>
              {lang === 'en' ? 'Order start time:' : 'Hora de inicio de la orden:'} <b>{new Date(orderStartTime).toLocaleString(lang === 'en' ? 'en-US' : 'es-MX', { timeZone: orderTimezone || undefined })}</b>
              {orderTimezone && (
                <div style={{ color: '#888', fontSize: 13 }}> ({orderTimezone})</div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Sección Plagas a tratar por apartamento */}
      <section>
        {/* <b>{L.pests}:</b> */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexWrap: 'wrap', margin: '2px 0' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <b>{L.treatment}:</b>
            <b style={{color: '#830303', fontSize: 16}}>{apartmentPests.map(p => p.name).join(', ')}</b>
          </div>
        </div>
      </section>

      {/* Detalles de visita */}
      <section>
        <b>{L.visitDetails}:</b>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
          <label><input type="checkbox" name="refused" checked={form.refused} onChange={handleChange} /> {L.refused}</label>
          <label><input type="checkbox" name="notOpened" checked={form.notOpened} onChange={handleChange} /> {L.notOpened}</label>
          <label><input type="checkbox" name="managerPresent" checked={form.managerPresent} onChange={handleChange} /> {L.managerPresent}</label>
        </div>
      </section>

      {/* Nivel de infestación */}
      <section>
        <b>{L.infestationLevel}:</b>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <label><input type="radio" name="infestation" value="low" checked={form.infestation === 'low'} onChange={handleChange} /> {L.low}</label>
          <label><input type="radio" name="infestation" value="medium" checked={form.infestation === 'medium'} onChange={handleChange} /> {L.medium}</label>
          <label><input type="radio" name="infestation" value="high" checked={form.infestation === 'high'} onChange={handleChange} /> {L.high}</label>
        </div>
      </section>

      {/* Áreas a tratar */}
      <section>
        <b>{L.areasToTreat}:</b>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          {areaOptions.map(area => (
            <label key={area}><input type="checkbox" checked={form.areas.includes(area)} onChange={() => handleAreaChange(area)} /> {L[area]}</label>
          ))}
        </div>
      </section>

      {/* Preparación */}
      <section>
        <b>{L.preparation}:</b>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          {prepOptions.map(prep => (
            <label key={prep}><input type="checkbox" checked={form.preparation.includes(prep)} onChange={() => handlePreparationChange(prep)} /> {L[prep]}</label>
          ))}
        </div>
      </section>

      {/* Observaciones / Tratamiento especial */}
      <section style={{ width: '100%' }}>
        <b>{L.specialTreatment}:</b>
        <div style={{ width: '100%', display: 'flex' }}>
          <textarea
            name="observations"
            value={form.observations}
            onChange={handleChange}
            style={{
              width: '100%',
              minHeight: 60,
              borderRadius: 4,
              border: '1px solid #ccc',
              padding: 8,
              resize: 'vertical',
              boxSizing: 'border-box',
              maxWidth: '100%'
            }}
          />
        </div>
      </section>
        
      {/* Fotos del servicio */}
      <section>
         <b>{L.photos}:</b>
         <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {photos.map((file, idx) => {
               const cacheKey = `${file?.name || 'photo'}-${file?.size || 0}-${file?.lastModified || idx}`;
               let previewUrl = file?.dataUrl;
               if (!previewUrl && file instanceof File) {
                 if (!photoPreviewUrlsRef.current.has(cacheKey)) {
                   photoPreviewUrlsRef.current.set(cacheKey, URL.createObjectURL(file));
                 }
                 previewUrl = photoPreviewUrlsRef.current.get(cacheKey);
               }
               return (
                 <div key={`${file?.name || 'photo'}-${idx}`} style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={previewUrl || ''} alt={`Foto ${idx + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #2563eb' }} />
                    <button type="button" onClick={() => handleRemovePhoto(idx)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: 22, height: 22, fontWeight: 700, cursor: 'pointer' }}>×</button>
                 </div>
               );
            })}
            {photos.length < 6 && (
               <button
                  type="button"
                  onClick={() => !compressingPhotos && fileInputRef.current.click()}
                  disabled={compressingPhotos}
                  style={{
                     width: 80,
                     height: 80,
                     borderRadius: 8,
                     border: '1px dashed #2563eb',
                     background: compressingPhotos ? '#e2e8f0' : '#f1f5f9',
                     fontWeight: 700,
                     color: compressingPhotos ? '#94a3b8' : '#2563eb',
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     cursor: compressingPhotos ? 'wait' : 'pointer',
                     opacity: compressingPhotos ? 0.6 : 1,
                     fontSize: 24
                  }}
               >
                  {compressingPhotos ? (
                     <div style={{ fontSize: 10, textAlign: 'center' }}>
                        {L.processing}
                     </div>
                  ) : (
                     <>
                        <span style={{ fontSize: 32 }}>📷</span>
                        <span style={{ fontSize: 10, marginTop: 4 }}>
                           {L.addPhoto}
                        </span>
                     </>
                  )}
               </button>
            )}
         </div>
         <input type="file" accept="image/*" multiple capture="environment" style={{ display: 'none' }} ref={fileInputRef} onChange={handlePhotoChange} />
         {compressingPhotos && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
               {L.compressingImages}
            </div>
         )}
      </section>

      {success && <div style={{ color: '#16a34a', fontWeight: 700 }}>{L.success}</div>}
      {error && <div style={{ color: 'red', fontWeight: 700 }}>{error}</div>}
      {showCleanMemory && (
        <button type="button" onClick={cleanPhotoMemory} style={{ marginTop: 8, background: '#f87171', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', fontWeight: 700 }}>
          {lang === 'en' ? 'Clean photo memory' : 'Limpiar memoria de fotos'}
        </button>
      )}
      <button type="submit" style={{ padding: '16px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: 28, position: 'relative', opacity: (compressingPhotos || saving) ? 0.7 : 1, cursor: (compressingPhotos || saving) ? 'not-allowed' : 'pointer' }} disabled={compressingPhotos || saving}>
        {saving ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="spinner" style={{
              width: 24, height: 24, border: '3px solid #fff', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block'
            }} />
            {uploadProgress > 0 ? `${lang === 'en' ? 'Saving' : 'Guardando'} (${uploadProgress}/${photos.length})` : (lang === 'en' ? 'Saving...' : 'Guardando...')}
          </span>
        ) : 'Done'}
      </button>
      {/* Spinner CSS */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
}
